import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import Seo from "@/components/Seo";

export default function ServiceLayout({
  slug,
  eyebrow,
  title,
  intro,
  image,
  imageAlt,
  whoFor,
  problems,
  capabilities,
  providerRole,
  conversation,
  faqs,
  ctaLabel,
  providerLink,
  extra,
}) {
  return (
    <>
      <Seo title={`${title} — Riverstone Partners`} description={intro.slice(0, 155)} path={`/solutions/${slug}`} />

      {/* Hero band */}
      <section className="border-b border-charcoal/10 bg-sage/50 py-16 md:py-24">
        <div className="shell grid items-end gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <p className="eyebrow text-forest">{eyebrow}</p>
            <h1 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-[1.1] tracking-tight text-navy sm:text-5xl" data-testid="service-page-title">
              {title}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-charcoal/80">{intro}</p>
            <Link to={`/contact?service=${slug}`} className="btn-primary mt-8" data-testid="service-inquiry-button">
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
            {providerLink && (
              <a
                href={providerLink.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-provider-link"
                className="mt-4 inline-flex min-h-[44px] items-center gap-2 pl-1 text-sm font-medium text-forest underline-offset-4 hover:underline"
              >
                {providerLink.label}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 17L17 7M7 7h10v10" /></svg>
              </a>
            )}
          </div>
          <div className="hidden lg:block">
            <img src={image} alt={imageAlt} width="800" height="600" loading="eager" className="aspect-[4/3] w-full object-cover shadow-soft" />
          </div>
        </div>
      </section>

      {/* Who it's for + image overlap (mobile shows image here) */}
      <section className="py-16 md:py-24">
        <div className="shell grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <Reveal className="lg:mt-10">
            <p className="eyebrow text-river">Who this is for</p>
            <ul className="mt-6 space-y-4">
              {whoFor.map((w, i) => (
                <li key={i} className="flex gap-3 text-charcoal/80">
                  <span className="mt-2 h-px w-6 shrink-0 bg-river" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
            <div className="mt-10 lg:hidden">
              <img src={image} alt={imageAlt} width="800" height="600" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="eyebrow text-river">Problems it helps address</p>
            <ol className="mt-6 space-y-6">
              {problems.map((p, i) => (
                <li key={i} className="border-b border-charcoal/10 pb-6 last:border-0" data-testid={`service-problem-${i}`}>
                  <span className="font-serif text-sm italic text-river">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-1 text-lg leading-relaxed text-charcoal/85">{p}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-mist py-16 md:py-24">
        <div className="shell">
          <Reveal>
            <p className="eyebrow text-forest">Relevant capabilities</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">
              What this solution can include
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {capabilities.map((c, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex min-h-[44px] items-start gap-3 border-b border-charcoal/10 py-4">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-forest" />
                  <div>
                    <p className="font-medium text-charcoal">{c.title}</p>
                    {c.body && <p className="mt-1 text-sm leading-relaxed text-charcoal/70">{c.body}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Provider role */}
      <section className="py-16 md:py-24">
        <div className="shell">
          <Reveal>
            <div className="border-l-2 border-forest bg-sage/50 p-8 md:p-12" data-testid="provider-role-callout">
              <p className="eyebrow text-forest">How delivery works</p>
              <div className="mt-6 grid gap-8 md:grid-cols-2">
                {providerRole.map((r, i) => (
                  <div key={i}>
                    <h3 className="font-serif text-xl text-forest">{r.title}</h3>
                    <p className="mt-3 leading-relaxed text-charcoal/80">{r.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-16">
            <p className="eyebrow text-river">The initial conversation</p>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">
              What we cover first
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {conversation.map((c, i) => (
                <div key={i} className="border border-charcoal/10 bg-white p-7">
                  <span className="font-serif text-3xl font-light text-river">{i + 1}</span>
                  <h3 className="mt-3 font-medium text-charcoal">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{c.body}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {extra}
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-mist py-16 md:py-24">
        <div className="shell max-w-4xl">
          <Reveal>
            <p className="eyebrow text-forest">Common questions</p>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">Frequently asked</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Faq items={faqs} />
          </Reveal>
          <Reveal delay={0.15} className="mt-12 text-center">
            <h3 className="font-serif text-2xl text-navy">Ready when you are.</h3>
            <Link to={`/contact?service=${slug}`} className="btn-primary mt-6" data-testid="service-bottom-cta">
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
